import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { MapPin, Users, TrendingUp, Star } from 'lucide-react';

const LocationBar = ({ location, data, maxCount }) => {
  const percentage = (data.count / maxCount) * 100;
  
  const getBarColor = () => {
    if (data.avg_score >= 75) return 'bg-green-500';
    if (data.avg_score >= 60) return 'bg-blue-500';
    if (data.avg_score >= 45) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  return (
    <div className="space-y-1.5 group">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
          <span className="truncate font-medium">{location}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge variant="outline" className="text-xs gap-1">
            <Users className="h-3 w-3" />
            {data.count}
          </Badge>
          <Badge className="text-xs bg-primary/10 text-primary border-primary/20">
            Avg: {data.avg_score}
          </Badge>
        </div>
      </div>
      
      <div className="h-6 bg-muted rounded-md overflow-hidden relative">
        <div 
          className={`h-full ${getBarColor()} transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
          <div className="flex gap-1">
            {data.strong_fit > 0 && (
              <span className="bg-green-600/80 text-white px-1.5 py-0.5 rounded text-[10px]">
                {data.strong_fit} Strong
              </span>
            )}
            {data.good_fit > 0 && (
              <span className="bg-blue-600/80 text-white px-1.5 py-0.5 rounded text-[10px]">
                {data.good_fit} Good
              </span>
            )}
          </div>
          {data.risky_fit + data.weak_fit > 0 && (
            <span className="text-muted-foreground text-[10px]">
              {data.risky_fit + data.weak_fit} Others
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const TalentHeatmap = ({ heatmapData, isLoading }) => {
  if (isLoading) {
    return (
      <Card data-testid="heatmap-loading">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!heatmapData || heatmapData.length === 0) {
    return (
      <Card data-testid="heatmap-empty">
        <CardContent className="py-8 text-center text-muted-foreground">
          <MapPin className="h-10 w-10 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
          <p className="text-sm">No location data available</p>
          <p className="text-xs mt-1">Analyze more resumes to see the talent distribution</p>
        </CardContent>
      </Card>
    );
  }

  const maxCount = Math.max(...heatmapData.map(d => d.count));
  const totalCandidates = heatmapData.reduce((sum, d) => sum + d.count, 0);
  const avgScore = Math.round(
    heatmapData.reduce((sum, d) => sum + d.avg_score * d.count, 0) / totalCandidates
  );

  return (
    <Card data-testid="talent-heatmap">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <MapPin className="h-4 w-4 text-primary" strokeWidth={1.5} />
            Talent Heatmap
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {heatmapData.length} Locations
            </Badge>
            <Badge className="text-xs bg-primary/10 text-primary border-primary/20">
              <TrendingUp className="h-3 w-3 mr-1" />
              Avg: {avgScore}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[280px] pr-4">
          <div className="space-y-4">
            {heatmapData.map((item, idx) => (
              <LocationBar 
                key={idx} 
                location={item.location} 
                data={item}
                maxCount={maxCount}
              />
            ))}
          </div>
        </ScrollArea>
        
        {/* Legend */}
        <div className="mt-4 pt-3 border-t flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500" />
            <span>75+ Avg</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-500" />
            <span>60-74</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-yellow-500" />
            <span>45-59</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-500" />
            <span>&lt;45</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TalentHeatmap;
